<template>
  <div
    v-if="characteristicsKeys.length"
    :class="['background', `background--${genre}`]"
  >
    <div class="container pt-56 pt-lg-80 pb-64 pb-lg-80 text-center">
      <div class="row d-flex justify-content-center pt-lg-16 pb-lg-16">
        <div class="col-md-6 col-lg-10">
          <h2 class="mb-32 mb-lg-64">
            {{ $label('books.characteristics.title') }}
          </h2>
          <div class="characteristics d-flex flex-wrap">
            <div
              v-for="characteristic in characteristicsKeys"
              :key="characteristic"
              class="characteristic d-flex flex-column justify-content-center align-items-center"
            >
              <div
                class="icon-background d-flex justify-content-center align-items-center"
              >
                <component :is="componentName(characteristic)" />
              </div>
              <p class="mb-0">
                {{ characteristics[characteristic] }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Bestseller from '~/assets/icons/bestseller.svg'
import Romantic from '~/assets/icons/romantic.svg'
import Emotional from '~/assets/icons/emotional.svg'
import Debut from '~/assets/icons/debut.svg'
import DutchAuthor from '~/assets/icons/dutch_author.svg'
import BookchoiceExclusive from '~/assets/icons/bookchoice_exclusive.svg'
import FilmAdaptation from '~/assets/icons/film_adaptation.svg'
import Literature from '~/assets/icons/literature.svg'
import Family from '~/assets/icons/family.svg'
import Classic from '~/assets/icons/classic.svg'
import Whodunit from '~/assets/icons/whodunit.svg'
import BestsellingAuthor from '~/assets/icons/bestselling_author.svg'
import EasyToRead from '~/assets/icons/easy_to_read.svg'
import HeavySubject from '~/assets/icons/heavy_subject.svg'
import Creepy from '~/assets/icons/creepy.svg'
import Friendship from '~/assets/icons/friendship.svg'
import Lgbtq from '~/assets/icons/lgbtq.svg'
import Exciting from '~/assets/icons/exciting.svg'
import UnreliableNarrator from '~/assets/icons/unreliable_narrator.svg'
import BackInTime from '~/assets/icons/back_in_time.svg'
import Pages400 from '~/assets/icons/pages_400.svg'
import Inspiring from '~/assets/icons/inspiring.svg'
import PersonalStory from '~/assets/icons/personal_story.svg'
import Feminist from '~/assets/icons/feminist.svg'
import Psychological from '~/assets/icons/psychological.svg'
import Sexy from '~/assets/icons/sexy.svg'
import Funny from '~/assets/icons/funny.svg'
import Mysterious from '~/assets/icons/mysterious.svg'

export default {
  components: {
    Bestseller,
    Romantic,
    Emotional,
    Debut,
    DutchAuthor,
    BookchoiceExclusive,
    FilmAdaptation,
    Literature,
    Family,
    Classic,
    Whodunit,
    BestsellingAuthor,
    EasyToRead,
    HeavySubject,
    Creepy,
    Friendship,
    Lgbtq,
    Exciting,
    UnreliableNarrator,
    BackInTime,
    Pages400,
    Inspiring,
    PersonalStory,
    Feminist,
    Psychological,
    Sexy,
    Funny,
    Mysterious,
  },
  props: {
    characteristics: {
      type: Object,
      required: true,
    },
    genre: {
      type: String,
      default: '',
    },
  },
  computed: {
    characteristicsKeys() {
      return Object.keys(this.characteristics)
    },
  },
  methods: {
    componentName(key) {
      const formattedKey = key
        .split('_')
        .map((part) => this.$capitalizeFirstLetter(part.toLowerCase()))
        .join('')

      return formattedKey
    },
  },
}
</script>

<style lang="scss" scoped>
@include generate-genre-classes('background-color', 'light', 'background', 0.5);

.characteristics {
  gap: 24px;
  justify-content: space-evenly;
}

.icon-background {
  background-color: $white;
  width: 144px;
  height: 152px;
  margin-bottom: 16px;
}

.characteristic {
  &:first-of-type {
    .icon-background {
      clip-path: path(
        'M125.944 46.3001C136.975 64.9944 145.351 80.774 143.819 97.1566C142.185 113.539 130.745 130.424 113.381 140.978C95.9145 151.43 72.4219 155.652 50.8701 148.315C29.3183 141.078 9.7071 122.283 2.86362 98.8652C-4.08199 75.4471 1.84221 47.4057 18.0827 27.6059C34.221 7.70549 60.7778 -3.8528 80.8997 1.17254C101.022 6.09738 114.811 27.6059 125.944 46.3001Z'
      );
    }
  }

  &:nth-of-type(2) {
    .icon-background {
      width: 128px;
      height: 136px;
      margin-bottom: 32px;
      clip-path: path(
        'M115.422 35.1294C127.26 54.3583 131.239 75.8669 125.223 92.1222C119.304 108.378 103.39 119.281 86.6998 126.913C69.9127 134.545 52.3494 139.005 36.0474 133.653C19.7455 128.399 4.60805 113.333 0.920719 95.9878C-2.76662 78.543 4.9962 58.7195 17.5137 39.3915C29.9342 19.9643 47.1095 1.03282 65.5461 0.0416367C83.9828 -0.949543 103.681 15.9005 115.422 35.1294Z'
      );
    }
  }

  &:nth-of-type(3) {
    .icon-background {
      width: 128px;
      height: 144px;
      margin-bottom: 24px;
      clip-path: path(
        'M119.162 120.724C131.681 103.216 129.262 77.0025 122.003 53.991C114.744 30.9794 102.646 10.9694 85.0781 3.56568C67.4047 -3.93809 44.3662 1.16446 28.4812 11.7698C12.5962 22.3751 4.07509 38.5832 1.12953 54.8914C-1.81604 71.0995 0.919124 87.5078 10.2818 104.416C19.7497 121.225 35.9503 138.733 57.6212 142.936C79.3974 147.238 106.749 138.233 119.162 120.724Z'
      );
    }
  }

  &:nth-of-type(4) {
    .icon-background {
      clip-path: path(
        'M18.056 46.3001C7.02468 64.9944 -1.35092 80.774 0.181204 97.1566C1.81547 113.539 13.2553 130.424 30.6194 140.978C48.0855 151.43 71.5781 155.652 93.1299 148.315C114.682 141.078 134.293 122.283 141.136 98.8652C148.082 75.4471 142.158 47.4057 125.917 27.6059C109.779 7.70549 83.2222 -3.8528 63.1003 1.17254C42.9785 6.09738 29.1894 27.6059 18.056 46.3001Z'
      );
    }
  }
}

p {
  max-width: 170px;
}

@include media-breakpoint-up(lg) {
  .characteristics {
    justify-content: space-between;
  }

  p {
    max-width: unset;
  }
}
</style>
